import * as CommonBrowserUtils from '@sparta.global.utilities/common-browser-utils-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaMediaQueryConstantsUtility from '@sparta.global.utilities/sparta-mediaquery-constants-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import _ from '@sparta.global.vendors/lodash';

/**
 * Simple model for our tag map for viewport-specific meta tags to facilitate generating the tags'
 * object.
 */
class TagMapModel {
  /**
   * Sets up a new instance of `TagMapModel`.
   * @param {String} tag - The type of tag we will be dealing with.
   * @param {String} val - The value of the attribute we will use in our selector.
   * @param {String} set - The attribute/property we will be setting.
   * @param {String} type - Whether we will be updating an attribute or property.
   * @param {String} [attr] - The name of the attribute we'll use in our selector.
   */
  constructor(tag, val, set, type, attr) {
    this.tag = tag;
    this.val = val;
    this.set = set;
    this.type = type;
    if (attr) {
      this.attr = attr;
    }
  }
}

/**
 * Returns a browser-prefixed screen density media query where needed.
 * @return {String} The media query identifier.
 */
const getPrefixedPixelRatio = () => {
  // generate element to test vendor prefixes
  const elem = document.createElement('sparta');

  // vendor-prefixed pixel density queries
  const prefixes = {
    webkitTransform: '-webkit-min-device-pixel-ratio: 2',
    MozTransform: 'min--moz-device-pixel-ratio: 2',
    oTransform: '-o-min-device-pixel-ratio: 2/1'
  };

  // default pixel density query
  let result = 'min-device-pixel-ratio: 2';

  // iterate through prefixes and check to see if vendor-prefixed query is needed
  Object.keys(prefixes).some(prefix => {
    // if the element contains this vendor-prefixed transform style attribute
    if (typeof elem.style[prefix] !== 'undefined') {
      // get our prefixed media query
      result = prefixes[prefix];

      // exit Array.some()
      return true;
    }
    return false;
  });

  // return necessary media query
  return result;
};

/**
 * @const utilityNamespace
 * @description namespace for our mediaquery utility
 */
const utilityNamespace = 'spartaMediaQueryUtility';

/**
 * @const breakpointEventName
 * @description string to hold name of breakpoint Change event
 */
const breakpointEventName = 'BREAKPOINT_CHANGE';

/**
 * @const widgetResizeEventName
 * @description string to hold name for widget Resizing event
 */
const widgetResizeEventName = 'WIDGET_RESIZE';

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */
const EVENTS = {
  [breakpointEventName]: `${breakpointEventName}.${utilityNamespace}`,
  [widgetResizeEventName]: `${widgetResizeEventName}.${utilityNamespace}`
};

/* global EVENTS getPrefixedPixelRatio TagMapModel */

/**
 * @const defaultResolutionMediaQueryString
 * @description string to hold non-retina media query definition
 */
const defaultResolutionMediaQueryString = `not screen and (${getPrefixedPixelRatio()}), not screen and (min-resolution: 192dpi), not screen and (min-resolution: 2dppx)`;

/**
 * @const retinaResolutionMediaQueryString
 * @description string to hold retina media query definition
 */
const retinaResolutionMediaQueryString = `only screen and (${getPrefixedPixelRatio()}), only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx)`;

/**
 * @const prefixedDefaultResolutionQuery
 * @description MediaQueryListModel object to store vendor-prefixed default resolution query
 */
const prefixedDefaultResolutionQuery = new spartaMediaQueryConstantsUtility.MediaQueryListModel(spartaMediaQueryConstantsUtility.constants.mediaQueryResolutionString, spartaMediaQueryConstantsUtility.constants.defaultResolutionName, defaultResolutionMediaQueryString);

/**
 * @const prefixedRetinaResolutionQuery
 * @description MediaQueryListModel object to store vendor-prefixed retina resolution query
 */
const prefixedRetinaResolutionQuery = new spartaMediaQueryConstantsUtility.MediaQueryListModel(spartaMediaQueryConstantsUtility.constants.mediaQueryResolutionString, spartaMediaQueryConstantsUtility.constants.retinaResolutionName, retinaResolutionMediaQueryString);

/**
 * @description this will become our Map that references the various media queries and viewports
 */
spartaMediaQueryConstantsUtility.mediaQueriesList.splice(spartaMediaQueryConstantsUtility.mediaQueriesList.length - 2, 2, prefixedDefaultResolutionQuery, prefixedRetinaResolutionQuery);

/**
 * @const mediaQueryDefinitions
 * @description methods that allow us to easily return booleans for our media queries
 * based on what viewports exist in our viewport string
 */
const mediaQueryDefinitions = {
  /**
   * @method mediaQueryDefinitions.xsmallUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xsmallUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xsmallViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xsmallViewportName),
  /**
   * @method mediaQueryDefinitions.xsmallOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xsmallOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xsmallViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xsmallViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallMidViewportName),
  /**
   * @method mediaQueryDefinitions.smallUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallViewportName),
  /**
   * @method mediaQueryDefinitions.smallOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.mediumViewportName),
  /**
   * @method mediaQueryDefinitions.smallMid
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallMid media query
   */
  // eslint-disable-next-line max-len
  [spartaMediaQueryConstantsUtility.constants.smallMidViewportName]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallMidViewportName),
  /**
   * @method mediaQueryDefinitions.smallMidUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallMidUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallMidViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallMidViewportName),
  /**
   * @method mediaQueryDefinitions.smallMidOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallMidOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallMidViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallMidViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallPlusViewportName),
  /**
   * @method mediaQueryDefinitions.smallPlus
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallPlus media query
   */
  // eslint-disable-next-line max-len
  [spartaMediaQueryConstantsUtility.constants.smallPlusViewportName]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallPlusViewportName),
  /**
   * @method mediaQueryDefinitions.smallPlusUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallPlusUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallPlusViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallPlusViewportName),
  /**
   * @method mediaQueryDefinitions.smallPlusOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for smallPlusOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.smallPlusViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.smallPlusViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.mediumViewportName),
  /**
   * @method mediaQueryDefinitions.mediumUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for mediumUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.mediumViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.mediumViewportName),
  /**
   * @method mediaQueryDefinitions.mediumOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for mediumOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.mediumViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.mediumViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.largeViewportName),
  /**
   * @method mediaQueryDefinitions.largeUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for largeUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.largeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.largeViewportName),
  /**
   * @method mediaQueryDefinitions.largeOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for largeOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.largeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.largeViewportName),
  /**
   * @method mediaQueryDefinitions.xlargeUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xlargeUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xlargeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xlargeViewportName),
  /**
   * @method mediaQueryDefinitions.xlargeOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xlargeOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xlargeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xlargeViewportName) && !element.classList.contains(spartaMediaQueryConstantsUtility.constants.xxlargeViewportName),
  /**
   * @method mediaQueryDefinitions.xxlargeUp
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xxlargeUp media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xxlargeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryUpString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xxlargeViewportName),
  /**
   * @method mediaQueryDefinitions.xxlargeOnly
   * @param {String} element the container element whose width we want to check
   * @returns {boolean} for xxlargeOnly media query
   */
  [`${spartaMediaQueryConstantsUtility.constants.xxlargeViewportName}-${spartaMediaQueryConstantsUtility.constants.mediaQueryOnlyString}`]: element => element.classList.contains(spartaMediaQueryConstantsUtility.constants.xxlargeViewportName)
};

/**
 * @class MediaQueryUtility
 * @description
 *  listen for media query changes via matchMedia listener
 *  set classes on root element for responsive CSS
 *  queries include: orientation, viewport size, resolution
 */
class MediaQueryUtility {
  /**
   * @method constructor
   * @param {String} [container=spartaContainerSelector]
   * query selector string for application container
   * @description Initializes new instance of MediaQueryUtility Class and set some defualt settings
   */
  constructor(container = spartaMediaQueryConstantsUtility.constants.spartaContainerSelector) {
    // the selector for our sparta application container
    this.spartaContainerSelector = container;

    // the list of media queries
    this.mediaQueries = spartaMediaQueryConstantsUtility.mediaQueriesList;

    // the list of viewport names
    this.viewportNames = spartaMediaQueryConstantsUtility.constants.viewportNames;

    // by default we'll assume we are not dealing with a widget
    this.isWidget = false;

    // by default we'll assume we are not dealing with an android device
    this.isAndroid = false;

    // since xlarge and xxlarge didn't exist,
    // we might need to use large meta tags for xlarge and xxlarge
    this.useLargeAsLargeUp = true;

    // initialize an empty Object for storing the matched query info
    this.matchedQuery = {};
  }

  /**
   * @property {Object} EVENTS exposes event name constant
   * @description getter for EVENTS constant
   * @returns {Object} EVENTS constant
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * @method init
   * @param {Node} [appRootEl] Sparta application container
   * @description set the application root element and setup events
   * @returns {Map} mediaQuery map
   */
  init(appRootEl) {
    try {
      console.group('Sparta Media Query Utility');
      console.info('**Initializing**');

      // if provided element is DOM Node
      if (appRootEl && appRootEl.nodeType) {
        this.appRootEl = appRootEl;
      } else {
        // if an appRootEl was not provided, use spartaContainerSelector
        this.appRootEl = document.querySelector(appRootEl) || document.querySelector(this.spartaContainerSelector);
        // eslint-disable-next-line no-console
        console.warn('Invalid App root received, defaulting to sparta container.');
      }

      // find nearest sparta container
      const spartaContainer = this.appRootEl.getAttribute('data-sparta-container') ? this.appRootEl : this.appRootEl.querySelector('[data-sparta-container]');
      if (!spartaContainer) {
        throw new Error('Unable to locate a sparta container');
      }

      // check if this is a widget or an application
      this.isWidget = spartaContainer.hasAttribute(spartaMediaQueryConstantsUtility.constants.spartaWidgetContainerString);
      // eslint-disable-next-line no-console
      console.info(this.isWidget ? 'Mounting Resize Events for Widget' : 'Mounting Resize Events for App');

      // check to see if this is an Android device
      this.isAndroid = CommonBrowserUtils.isAndroid();

      // configure our resize/matchMedia/etc. listeners
      this.setupWindowResizeEvents();

      // get container ready
      this.removeLoadingClass();
      console.groupEnd();
    } catch (e) {
      throw new Error('Error Occured when Initializing Sparta Media Query.', e);
    }
  }

  /**
   * @method removeLoadingClass
   * @description remove the media query utility loading class from the application container
   * @returns {void}
   */
  removeLoadingClass() {
    this.appRootEl.classList.remove(spartaMediaQueryConstantsUtility.constants.utilityLoadingClass);
  }

  /**
   * Adds `matchMedia` listeners for screen size changes.
   * @returns {void}
   */
  setupWindowResizeEvents() {
    // Check to see if we are dealing with a widget or a site.
    if (!this.isWidget) {
      // Check to see if this is an Android device.
      if (this.isAndroid) {
        // For an Android device, rememeber the previous viewport width to check for keyboard open.
        this.prevWidth = this.storedWidth || window.innerWidth;
        this.storedWidth = window.innerWidth;

        // Recapture viewport width when user turns the device.
        window.addEventListener('orientationchange', () => {
          const afterOrientationChange = function afterOrientationChange() {
            if (this.storedWidth !== window.innerWidth) {
              this.prevWidth = this.storedWidth;
              this.storedWidth = window.innerWidth;
            }
            window.removeEventListener('resize', afterOrientationChange);
          };
          window.addEventListener('resize', afterOrientationChange.bind(this));
        });
      }

      // Set up `matchMedia` and listeners.
      this.mediaQueries.forEach(({
        name,
        type,
        mediaQuery
      }) => {
        const mql = window.matchMedia(mediaQuery);

        // Developer note 2020:
        // `addListener` has been tuned to be deprecated by all major browsers except IE.
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList/onchange
        if (!mql.addEventListener) {
          // If the browser does *not* support `addEventListener` (IE), use `addListener`.
          mql.addListener(this.handleResize.bind(this, name, type));
        } else if (this.isAndroid) {
          // Need to allow for time on Android.
          mql.addEventListener('change', () => setTimeout(() => {
            this.handleResize(name, type, mql);
          }, 0));
        } else {
          mql.addEventListener('change', this.handleResize.bind(this, name, type));
        }
        this.handleResize(name, type, mql, true);
      });

      // Publish a single `BREAKPOINT_CHANGE` event on load.
      spartaEventsUtility.pub(this.EVENTS.BREAKPOINT_CHANGE, this.matchedQuery);
    } else {
      // Instead of using `matchMedia` here, we will deal with the actual window resizing.
      const resizeHandler = _.debounce(_.bind(this.respondToContainerSize, this), spartaMediaQueryConstantsUtility.constants.spartaWidgetResizeDebounceDelay);
      window.addEventListener('resize', resizeHandler);

      // Set classes based on widget container's size.
      this.respondToContainerSize();

      // Listen for triggering of the `WIDGET_RESIZED` event.
      spartaEventsUtility.sub(this.EVENTS.WIDGET_RESIZE, this.respondToContainerSize.bind(this));
    }
  }

  /**
   * Responds to `matchMedia` events and updates media query info as needed in the store and then
   * emits `spartaBreakpointChange` if not suppressed.
   * @param {String} mqName - Name of viewport size from `mediaQueriesList` map.
   * @param {String} mqType - Name of type of media query from `mediaQueriesList` map.
   * @param {MediaQueryList} mqlList - `matchMedia` media query list.
   * @param {Boolean} [initial=false] Is this the first time `handleResize` was called?
   * @returns {void}
   */
  handleResize(mqName, mqType, mqlList, initial = false) {
    if (mqlList.matches) {
      // Set `currentMQ` in the store.
      this.matchedQuery[mqType] = mqName;
      this.appRootEl.classList.add(mqName);
    } else {
      this.appRootEl.classList.remove(mqName);
    }

    // Avoid firing a `BREAKPOINT_CHANGE` for keyboard hide/show on Android.
    const suppress = this.isAndroid && this.prevWidth === window.innerWidth;
    this.afterResize(suppress || initial);
  }

  /**
   * @method respondToContainerSize
   * @description adjusts viewport-specific classes to widget container
   * @returns {void}
   */
  respondToContainerSize() {
    const dimensions = this.appRootEl.getBoundingClientRect();
    this.mediaQueries.forEach(({
      name,
      type,
      width,
      mediaQuery
    }) => {
      switch (type) {
        case spartaMediaQueryConstantsUtility.constants.mediaQueryViewportString:
          if (dimensions.width >= width) {
            this.appRootEl.classList.add(name);

            // set currentMQ in store
            this.matchedQuery[type] = name;
          } else {
            this.appRootEl.classList.remove(name);
          }
          break;
        case spartaMediaQueryConstantsUtility.constants.mediaQueryResolutionString:
          if (window.matchMedia(mediaQuery).matches) {
            this.appRootEl.classList.add(name);

            // set currentMQ in store
            this.matchedQuery[type] = name;
          } else {
            this.appRootEl.classList.remove(name);
          }
          break;
        case spartaMediaQueryConstantsUtility.constants.mediaQueryOrientationString:
          if (name === spartaMediaQueryConstantsUtility.constants.portraitOrientationName) {
            if (dimensions.height > dimensions.width) {
              this.appRootEl.classList.add(name);

              // set currentMQ in store
              this.matchedQuery[type] = name;
            } else {
              this.appRootEl.classList.remove(name);
            }
          } else if (name === spartaMediaQueryConstantsUtility.constants.landscapeOrientationName) {
            if (dimensions.width > dimensions.height) {
              this.appRootEl.classList.add(name);

              // set currentMQ in store
              this.matchedQuery[type] = name;
            } else {
              this.appRootEl.classList.remove(name);
            }
          }
          break;
      }
    });
    this.afterResize();
  }

  /**
   * @method afterResize
   * @param {Boolean} [suppressEvent=false] should we suppress the BREAKPOINT_CHANGE event
   * @description common functionality that runs after window or widget container resize
   * @returns {void}
   */
  afterResize(suppressEvent = false) {
    // get current viewport and media query info
    const {
      currentViewport,
      currentResolution,
      currentOrientation,
      mediaQueryInfo
    } = this.getCurrentViewport();
    Object.keys(mediaQueryInfo).forEach(item => {
      if (mediaQueryInfo[item]) {
        this.appRootEl.classList.add(item);
      } else {
        this.appRootEl.classList.remove(item);
      }
    });

    // generate object with correct key/value relationships to update Store data for media queries
    const storeObject = {
      [spartaMediaQueryConstantsUtility.constants.mediaQueryViewportString]: currentViewport,
      [spartaMediaQueryConstantsUtility.constants.mediaQueryResolutionString]: currentResolution,
      [spartaMediaQueryConstantsUtility.constants.mediaQueryOrientationString]: currentOrientation,
      [spartaMediaQueryConstantsUtility.constants.spartaStoreMediaQueriesKey]: mediaQueryInfo
    };

    // update Store data
    spartaStoreUtility.updateData(`${spartaMediaQueryConstantsUtility.constants.spartaStoreNamespace}`, storeObject);

    // if we are dealing with a site
    if (!this.isWidget) {
      // update viewport-specific meta tags
      this.setViewportMetaTags(currentViewport);
    }

    // unless we want to supress the BREAKPONT_CHANGE event
    if (!suppressEvent) {
      // publish our spartaBreakpointChange event
      spartaEventsUtility.pub(this.EVENTS.BREAKPOINT_CHANGE, storeObject);
    }
  }

  /**
   * @method getCurrentViewport
   * @description parse string of matched viewports and return the current viewport
   * @returns {Object} the currentViewport info and the overall media query info
   */
  getCurrentViewport() {
    let currentViewport = spartaMediaQueryConstantsUtility.constants.smallViewportName;
    let currentResolution = spartaMediaQueryConstantsUtility.constants.defaultResolutionName;
    let currentOrientation = spartaMediaQueryConstantsUtility.constants.portraitOrientationName;
    const mediaQueryInfo = {};
    this.mediaQueries.forEach(({
      type,
      name
    }) => {
      // sometimes this matches as false, even though it will always be true
      // so we're just overriding that behavoir
      if (name === spartaMediaQueryConstantsUtility.constants.smallViewportName) {
        mediaQueryInfo[name] = true;
      } else {
        mediaQueryInfo[name] = this.appRootEl.classList.contains(name);
      }
      switch (type) {
        case spartaMediaQueryConstantsUtility.constants.mediaQueryViewportString:
          if (this.appRootEl.classList.contains(name)) {
            currentViewport = name;
          }
          break;
        case spartaMediaQueryConstantsUtility.constants.mediaQueryOrientationString:
          if (this.appRootEl.classList.contains(name)) {
            currentOrientation = name;
          }
          break;
        case spartaMediaQueryConstantsUtility.constants.mediaQueryResolutionString:
          if (this.appRootEl.classList.contains(name)) {
            currentResolution = name;
          }
          break;
      }
    });

    // iterate over media query definitions to set properties like "smallUp," "smallOnly," etc.
    Object.keys(mediaQueryDefinitions).forEach(query => {
      // run the definition method and pass the container element
      mediaQueryInfo[query] = mediaQueryDefinitions[query](this.appRootEl);
    });

    // return an object of the currentViewport info and the overall media query info
    return {
      currentViewport,
      currentResolution,
      currentOrientation,
      mediaQueryInfo
    };
  }

  /**
   * @method setViewportMetaTags
   * @param {String} [currentViewport="default"] the string for the current viewport
   * @description updates relevant meta tags with viewport-specific data
   * @returns {boolean} false if exit early, otherwise true
   */
  setViewportMetaTags(currentViewport = 'default') {
    // if this is a widget, exit
    if (this.isWidget) {
      return false;
    }

    // if there isn't a metaTags object, exit
    if (typeof window.sparta.metaTags === 'undefined') {
      return false;
    }

    /**
     * @const propertyTypeString
     * @description string for denoting type of value being set is property
     */
    const propertyTypeString = 'property';

    /**
     * @const attributeTypeString
     * @description string for denoting type of value being set is attribute
     */
    const attributeTypeString = 'attribute';

    /**
     * @const defaultMetaTagString
     * @description string for default viewport-specific meta tags
     */
    const defaultMetaTagString = 'default';

    /**
     * @const tagMap
     * @description Array of objects mapping out the tags in the `<head>`
     * that we may have viewport-specific content for
     * @example {
     *  tag: 'meta', // what kind of html tag are we looking for
     *  attr: 'name', // what attribute of that element do we need to look at
     *  val: 'keywords' // what value should that attribute have
     *  set: 'content' // what attribute are we going to set
     * }
     */
    const tagMap = [new TagMapModel('title', 'title', 'innerHTML', propertyTypeString), new TagMapModel('link', 'canonical', 'href', attributeTypeString, 'rel'), new TagMapModel('meta', 'keywords', 'content', attributeTypeString, 'name'), new TagMapModel('meta', 'description', 'content', attributeTypeString, 'name'), new TagMapModel('meta', 'og:title', 'content', attributeTypeString, 'property'), new TagMapModel('meta', 'og:type', 'content', attributeTypeString, 'property'), new TagMapModel('meta', 'og:image', 'content', attributeTypeString, 'property'), new TagMapModel('meta', 'og:description', 'content', attributeTypeString, 'property'), new TagMapModel('meta', 'og:url', 'content', attributeTypeString, 'property'), new TagMapModel('meta', 'og:site_name', 'content', attributeTypeString, 'property')];

    // iterate through tagMap Array and set relevant metadata
    tagMap.forEach(({
      tag,
      attr,
      val,
      set,
      type
    }) => {
      // get meta tag value from global metaTags object
      let value = window.sparta.metaTags[val];

      // if we don't have a metaTag value for this tag, exit
      if (typeof value === 'undefined') {
        return false;
      }

      // if this metaTag has an object with viewport-specific values
      if (Object.prototype.hasOwnProperty.call(value, currentViewport)) {
        // get the value based on the current viewport
        value = value[currentViewport];
      } else if (typeof value !== 'string') {
        // if this metaTag value is an object but there is no value for this viewport, get the default value
        // if we need to use large as large-up,
        // check to see if the viewport is xlarge or xxlarge and if large metadata exists
        if (this.useLargeAsLargeUp && value[spartaMediaQueryConstantsUtility.constants.viewportNames.large] && (currentViewport === spartaMediaQueryConstantsUtility.constants.viewportNames.xlarge || currentViewport === spartaMediaQueryConstantsUtility.constants.viewportNames.xxlarge)) {
          value = value[spartaMediaQueryConstantsUtility.constants.viewportNames.large];
        } else {
          value = value[defaultMetaTagString];
        }
      }

      // combined tag, attribute, and value strings into the relevant selector
      // for the element we need to update
      const selector = `${tag}${attr ? `[${attr}${val ? `="${val}"` : ''}]` : ''}`;

      // attempt to select the element whose selector we created above
      const tagElement = document.querySelector(selector);

      // if the element exists
      if (tagElement) {
        // update the relevant attribute or property
        if (type === attributeTypeString) {
          tagElement.setAttribute(set, value);
        } else if (type === propertyTypeString) {
          tagElement[set] = value;
        }
      } else {
        // create the relevant tag
        const generatedTag = document.createElement(tag);
        if (type === attributeTypeString) {
          generatedTag.setAttribute(set, value);
          generatedTag.setAttribute(attr, val);
        } else if (type === propertyTypeString) {
          generatedTag[set] = value;
        }

        // append created element to the `<head>`
        document.head.append(generatedTag);
      }
      return true;
    });
    return true;
  }
}

/**
 * @const spartaMediaQueryUtility
 * @description single isntance of MediaQueryUtility used to expose this utility
 */
const mediaQueryUtility = new MediaQueryUtility();

export { mediaQueryUtility as default };
